import { useSyncExternalStore, useCallback, useRef } from "react";

import { storageSubscription } from "./storageSubscription";

type SetValueParam<T> = T | ((prevValue: T) => T);

type StorageItem<T> = {
    key: string;
    initialValue: T;
};

type StorageConfig<T> = {
    item: StorageItem<T>;
    storage?: Storage;
    onError?: (error: Error) => void;
};

const defaultErrorHandler = (error: Error): void => {
    console.error("Storage operation failed:", error);
};

export function useStorage<T>({
    item: { key, initialValue },
    storage = window.localStorage,
    onError = defaultErrorHandler,
}: StorageConfig<T>): readonly [T, (value: SetValueParam<T>) => void] {
    const latestSnapshot = useRef<T>(initialValue);

    const getSnapshot = useCallback((): T => {
        try {
            const item = storage.getItem(key);
            if (!item) {
                return latestSnapshot.current;
            }

            const parsed = JSON.parse(item);
            latestSnapshot.current = parsed;

            return parsed;
        } catch (error) {
            onError(error instanceof Error ? error : new Error("Failed to parse storage value"));
            return latestSnapshot.current;
        }
    }, [key, storage, onError]);

    const subscribe = useCallback(
        (callback: () => void) => {
            return storageSubscription.subscribe(key, callback);
        },
        [key],
    );

    const value = useSyncExternalStore(subscribe, getSnapshot);

    const setValue = useCallback(
        (newValue: SetValueParam<T>): void => {
            try {
                const nextValue = newValue instanceof Function ? newValue(latestSnapshot.current) : newValue;
                latestSnapshot.current = nextValue;

                storage.setItem(key, JSON.stringify(nextValue));

                storageSubscription.notify(key);
            } catch (error) {
                onError(error instanceof Error ? error : new Error("Failed to set storage value"));
            }
        },
        [key, storage, onError],
    );

    return [value, setValue] as const;
}
